import React, { createContext, useContext } from 'react';

/**
 * 📱 A phone-only note shown right after the title (owner 2026-10-03: the
 * phone order table's 'Due ● 08 Aug 26' moved INTO the Order Details header).
 * The phone row PROVIDES it; the header only renders what it is given.
 */
export const OrderHeaderNoteContext = createContext(null);

/**
 * `OrderSectionHeader` — the "Order Details" section-header bar, extracted
 * (2026-08-16) so `OrderForm`'s edit-branch header and the shared read-only
 * panel's (`OrderExpandedRow`) new header cannot drift onto two class
 * strings. Product owner: *"I asked before for the departments orders and my
 * orders to look as similar as possible and share code where possible."*
 *
 * 🔑 **TWO BRANCHES, ONE CLASS STRING PAIR (`SECTION_BAR`/`TITLE`), AND THE
 * `null` BRANCH IS BYTE-IDENTICAL TO WHAT `OrderForm.js` SHIPPED BEFORE THIS
 * EXTRACTION.** With no `actions` this renders a single `<h3>` carrying
 * `${TITLE} ${SECTION_BAR}` and `data-section-title` — proven, not claimed,
 * by `OrderForm.headerActions.test.js` §1 against the literal class string
 * that shipped in commit `14374e9f`. **Never let a caller hand-copy these
 * class strings into a second location** — import `SECTION_BAR`/`TITLE` or
 * this component instead.
 *
 * 🪤 **`data-section-title` MOVES TO THE WRAPPER IN THE ACTIONS BRANCH, AND
 * IT MUST.** `client/src/index.css:202-208` is `html.dark [data-section-title]
 * { background-color: rgba(51,65,85,0.3) !important }` — a DARK-MODE-ONLY
 * fill. Left on the inner `<h3>` it paints the title and stops dead before
 * the actions: a half-painted bar, for dark-mode users alone, invisible to
 * any light screenshot. Same attribute-driven `index.css` table already
 * recorded against `OrderExpandedDetails`'s own section headers.
 *
 * 🚨 **`-mx-2.5 -mt-2.5` TRAVELS WITH THE BAR, NOT WITH THE TITLE.** It
 * cancels the card's own `p-2.5` so the header spans the full card width and
 * its `border-b` reads as the card's own divider. Left on the `<h3>` inside a
 * flex row it would drag the title out past the card edge and leave the
 * actions inset.
 *
 * 📱 **AT 375 THE BAR WRAPS: title on line 1, actions full-width on line
 * 2.** The actions node is `w-full md:w-auto`; above `md` it right-aligns via
 * `md:ml-auto` on its own wrapper — not `justify-between` on the parent —
 * because `ml-auto` right-aligns whatever else is or is not rendered beside
 * the title, and leaves the wrapped mobile row alone (below `md` the actions
 * are `w-full`, so there is no space to push into).
 *
 * `actionsTestId` is REQUIRED whenever `actions` is passed — every caller
 * must supply its own distinct id (`order-form-header-actions` for the edit
 * form, `${testIdPrefix}-readonly-header-actions` for the read-only panel)
 * so surfaces never collide on one testid.
 */
export const SECTION_BAR = '-mx-2.5 -mt-2.5 px-2 py-1 border-b border-gray-200 dark:border-gray-700 rounded-t-lg';
export const TITLE = 'text-lg font-semibold text-gray-700 dark:text-gray-200';

/**
 * 📱 BELOW `md` THE ORDER'S ACTIONS MENU SITS ON THE TITLE'S LINE, RIGHT-ALIGNED
 * (owner, 2026-10-01: "never wrapped underneath it"). It used to take a full-width
 * second row (`w-full`, then the page's `grid grid-cols-2`). Now the actions slot
 * and the page's wrapper are `display: contents`, so their children are flex items
 * of this bar: the menu (`[data-order-actions-menu]`, set by `OrderActionsMenu`)
 * gets `order-1` + `ml-auto`; a zero-height `::after` line break follows it; any
 * OTHER button (Approve / Reject / Override) gets `order-2` and shares the next
 * row. Every class is a `max-md:` variant — md+ is unchanged.
 * ⚠️ Assumes the page hands ONE wrapper `<div>` (all three do). Pinned by
 * `OrderSectionHeader.mobile.test.js`; the geometry is a browser check.
 */
export const MOBILE_HEADER_BAR =
  "max-md:gap-y-0 max-md:after:content-[''] max-md:after:basis-full max-md:after:order-1";
export const MOBILE_HEADER_ACTIONS =
  'max-md:contents max-md:[&>div:not([data-order-actions-menu])]:contents ' +
  'max-md:[&>div>*]:order-2 max-md:[&>div>*]:mt-2 max-md:[&>div>*]:flex-1 ' +
  'max-md:[&>div>[data-order-actions-menu]]:order-1 max-md:[&>div>[data-order-actions-menu]]:mt-0 ' +
  'max-md:[&>div>[data-order-actions-menu]]:flex-none max-md:[&>div>[data-order-actions-menu]]:ml-auto ' +
  'max-md:[&>div>[data-order-actions-menu]]:w-auto';

/**
 * 🖥️ EVERY SECTION OF AN OPENED ORDER LOOKS ALIKE ON DESKTOP (owner 2026-10-10,
 * on staging: *"each section when collapased should be in a similar style to the
 * next"* — *"dont change the fundimentals on desktop just make the sections look
 * similar to eachother"*). From `md` up, Notes / Attachments / Delivery
 * Paperwork / Activity History / Order Progress each sit in their OWN white card
 * (`DESKTOP_SECTION_CARD`, the Order Details card's look) with a chevron-first
 * header whose title matches Order Details' (`DESKTOP_SECTION_TITLE`,
 * `DESKTOP_SECTION_CHEVRON`). Every class is an `md:` variant, so the phone —
 * which has its own one-bar rule in `index.css` (`[data-mobile-compact]`) — is
 * untouched. Import these; never re-type them at a section.
 */
// 🪤 DARK MODE COPIES index.css'S OVERRIDES BY HAND, because they key on the BARE
// class (`html.dark .bg-white`, `.shadow-md`, `.text-gray-700`) and an `md:` class
// never matches them. Measured 2026-10-10 against the neighbouring cards: bg
// #1e293b (slate-800, not gray-800), a 1.5px white/20 border, title #d1d5db.
// 🪤 THE TITLE IS A HOOK CLASS, NOT TAILWIND, FOR TWO MEASURED REASONS: (1) its
// dark colour cannot be a `dark:` class — that compiles to `:is(.dark *)`, which
// LOSES to `html.dark .text-gray-600 {…!important}` on the section's base class;
// (2) an `md:text-*` class EXEMPTS the label from index.css's phone shrink
// (`[data-mobile-compact] .text-sm:not([class*="md:text-"])`), which made Notes and
// Activity 14px on a phone beside 12px neighbours. So index.css styles
// `.desktop-section-title` at md+ (size, weight, light AND dark colour).
// 🔀 2026-10-10 (owner, dark mode: Line Items + Notes "seem to have more of a drop
// shadow"): the card's dark shadow + border are ALSO index.css's job now, via the
// `desktop-section-card` hook — a `dark:` class lost to `html.dark .border-gray-200`
// and could not reach `html.dark .shadow-md`'s heavier shadow (measured 0.1 vs 0.3).
export const DESKTOP_SECTION_CARD = 'desktop-section-card md:bg-white md:dark:bg-slate-800 md:rounded-lg md:shadow-md md:p-2.5';
export const DESKTOP_SECTION_TITLE = 'desktop-section-title';
// 🖥️ THE HEADER ROW of every section (owner 2026-10-10: "Collapsed sections need a
// hover over color on desktop"). `desktop-section-head` = one hover tint, one dark
// tint, pointer — index.css, md+ only. `desktop-section-head-bleed` is for a header
// that sits INSIDE a padded card (Notes, Attachments, Activity, Order Progress): it
// bleeds to the card's edges so the hover fills the bar, and folded (its own
// `aria-expanded="false"`) it IS the card. Order Details / Line Items are already
// full-bleed bars and take only the first class.
export const DESKTOP_SECTION_HEAD = 'desktop-section-head';
export const DESKTOP_SECTION_HEAD_BLEED = 'desktop-section-head desktop-section-head-bleed';
export const DESKTOP_SECTION_CHEVRON = 'md:h-4 md:w-4';

// 🔀 `collapse` (2026-10-09, owner: desktop sections fold like the phone's):
// `{ open, onToggle, controls }` makes the TITLE a fold toggle with a chevron
// at md+ (below md the form keeps its own "Details" toggle). Absent → the
// header renders exactly as before.
function CollapsibleTitle({ title, collapse, className }) {
  return (
    // `md:flex`: an inline-flex button in a block <h3> adds a 2px baseline strut —
    // measured 50px against every other section header's 48 (2026-10-10).
    <h3 className={`${className} md:flex`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); collapse.onToggle(); }}
        aria-expanded={collapse.open}
        aria-controls={collapse.controls}
        // Below md the form's own "Details" toggle owns this; take the title
        // out of the tab order there too, not just the pointer (review M5).
        tabIndex={typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? -1 : undefined}
        className="max-md:pointer-events-none inline-flex items-center gap-2 text-left"
        data-testid="order-details-desktop-toggle"
      >
        <svg data-chevron xmlns="http://www.w3.org/2000/svg" className={`max-md:hidden h-4 w-4 text-gray-400 transition-transform ${collapse.open ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
        {title}
      </button>
    </h3>
  );
}

function OrderSectionHeader({ title = 'Order Details', actions = null, actionsTestId, collapse = null }) {
  // 📱 Inside a phone order row (the row PROVIDES `{ note }`), this header
  // becomes the row's EXTRA LINE (owner 2026-10-03): no title, the note
  // ("Due ● date") left, the actions right, joined to the row's card by
  // index.css (`[data-phone-row-extension]`). Desktop: unchanged.
  const rowCtx = useContext(OrderHeaderNoteContext);
  const inPhoneRow = rowCtx != null;
  // 🔻 The "Lead …" note is DELETED (owner 2026-10-19): the Lead Time field
  // shows inside Details on phone, so the note only repeated it. "Due" stays.
  const noteNode = rowCtx && rowCtx.note ? (
    <span className="md:hidden inline-flex flex-wrap items-center gap-x-1.5">
      {rowCtx.note}
    </span>
  ) : null;
  // 🖥️ A FOLDABLE header is the SAME SIZE as every other section's on desktop
  // (owner 2026-10-10: "the order details header is bigger as it accommodated
  // the action button … match the other section sizes"). md+ only:
  //  - the bar pads 10px like the other cards (`md:py-2.5`), and the actions
  //    overlap that padding (`md:-my-[5px]`: a 38px button in a 28px title line)
  //    instead of stretching it — measured 57px → 48px folded, Notes is 48;
  //  - folded, the bar IS the card: no divider, and it eats the card's bottom
  //    padding (`md:-mb-2.5`);
  //  - the WHOLE bar folds it, not just the chevron + title ("make sure clicking
  //    the header collapses the section"), except clicks on the actions. Desktop
  //    only: below md this bar is the phone row's extension line, whose "Details"
  //    fold is a separate toggle sharing the same state.
  const deskFold = collapse ? ` ${DESKTOP_SECTION_HEAD} md:py-2.5${collapse.open ? '' : ' md:border-b-0 md:-mb-2.5 md:rounded-b-lg'}` : '';
  // 📱 Below md the bar is the row's extension line, so a tap on it (not on
  // its buttons) is a tap on the ROW: it closes the order (owner 2026-10-10:
  // the ORD number opened and closed the order, the header only opened it).
  const onRowTap = inPhoneRow ? rowCtx.onRowTap : null;
  const onBarClick = (collapse || onRowTap) ? (e) => {
    const desktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
    if (!desktop) {
      if (onRowTap && !(e.target.closest && e.target.closest('[data-section-actions], button, a, input, select, textarea, label'))) onRowTap();
      return;
    }
    if (!collapse) return;
    if (e.target.closest && e.target.closest('[data-section-actions]')) return;
    collapse.onToggle();
  } : undefined;
  if (!actions && !inPhoneRow) {
    return collapse
      ? <div className={`${SECTION_BAR}${deskFold}`} data-section-title onClick={onBarClick}><CollapsibleTitle title={title} collapse={collapse} className={TITLE} /></div>
      : <h3 className={`${TITLE} ${SECTION_BAR}`} data-section-title>{title}</h3>;
  }
  return (
    <div
      className={`${SECTION_BAR}${deskFold} flex flex-wrap items-center gap-x-3 gap-y-2 ${MOBILE_HEADER_BAR}`}
      data-section-title
      onClick={onBarClick}
      {...(inPhoneRow ? { 'data-phone-row-extension': '' } : {})}
    >
      {collapse
        ? <CollapsibleTitle title={title} collapse={collapse} className={`${TITLE}${inPhoneRow ? ' max-md:hidden' : ''}`} />
        : <h3 className={`${TITLE}${inPhoneRow ? ' max-md:hidden' : ''}`}>{title}</h3>}
      {noteNode}
      {actions ? <div className={`w-full md:w-auto md:ml-auto${collapse ? ' md:-my-[5px]' : ''} ${MOBILE_HEADER_ACTIONS}`} data-testid={actionsTestId} data-section-actions="">{actions}</div> : null}
    </div>
  );
}

export default OrderSectionHeader;
